// src/notifications/services/use-notifications.ts
// POST /api/Notifications/UserNotifications — the paged notification inbox.
//
// Mirrors Flutter `NotificationCenterDataSourceImpl.getUserNotifications`
// (notification_center_data_source_impl.dart:25-38): POST with body
// { pageNumber, pageSize }, returns List<NotificationModel>. Although the verb
// is POST it is a read-style endpoint (no side effects), so it is wired as an
// infinite query — the same "read via POST" treatment as usePatientDocuments.
//
// Pagination faithful to the Flutter bloc (`fetchingMoreNotifications` +
// `updateNotifications`): a page shorter than `pageSize` means there is no more
// to load (`hasMoreItems = false`). The bloc also de-dupes by id and sorts by
// createdOn desc; we replicate both in `flattenNotifications` below.
import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import {
  type NotificationModel,
  type GetUserNotificationsRequest,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '@/notifications/types/notification';

/**
 * Root key for every notification query. All notification caches (list +
 * unseen-count) hang off this so a single prefix `invalidateQueries` covers
 * them. The concrete query key is ALWAYS scoped by the authenticated
 * `userId` (see `useNotifications` / `useUnseenNotificationCount`) — the
 * QueryClient is a long-lived singleton shared across logins, so an
 * identity-free key would serve user A's cached notification PHI to user B
 * after a logout/login within the cache lifetime. Scoping by userId keys
 * each user's inbox to its own cache line.
 */
export const notificationsQueryKey = ['notifications'] as const;

export function useNotifications(params: {
  pageSize?: number;
  enabled?: boolean;
}): UseInfiniteQueryResult<{ pages: NotificationModel[][]; pageParams: number[] }, Error> {
  const { pageSize = DEFAULT_PAGE_SIZE, enabled = true } = params;
  // Scope the cache to the signed-in user so cached PHI never crosses a
  // logout/login boundary on the shared QueryClient. No user → nothing to load.
  const userId = useAuthStore((s) => s.user?.userId);

  return useInfiniteQuery({
    queryKey: [...notificationsQueryKey, 'list', userId ?? 'anonymous', pageSize],
    enabled: enabled && userId != null,
    initialPageParam: DEFAULT_PAGE_NUMBER,
    queryFn: ({ pageParam, signal }) => {
      const body: GetUserNotificationsRequest = { pageNumber: pageParam, pageSize };
      return post<NotificationModel[]>('/api/Notifications/UserNotifications', body, { signal });
    },
    // Faithful to `updateNotifications`: a full page implies there may be more;
    // a short/empty page ends pagination.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === pageSize ? allPages.length + 1 : undefined,
  });
}

/**
 * De-dupe by id + sort by createdOn desc, mirroring the Flutter bloc's
 * `updateNotifications`. Pure so the panel and any test can reuse it.
 */
export function flattenNotifications(pages: NotificationModel[][] | undefined): NotificationModel[] {
  if (!pages) return [];
  const byId = new Map<string, NotificationModel>();
  for (const page of pages) {
    for (const n of page) {
      if (!byId.has(n.id)) byId.set(n.id, n);
    }
  }
  return [...byId.values()].sort(
    (a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
  );
}
